import React, { useState, useSyncExternalStore } from 'react';
import type { SupportedLanguage } from '@/libs/i18n';
import { getLocalizedCountryName } from './formConfig';
import { PHONE_COUNTRIES } from './phoneOptions';
import styles from './ContactUsForm.module.scss';

type SharedFieldProps = {
  error?: string;
  formId: string;
  label: string;
  name: string;
  required?: boolean;
};

type SelectOption = {
  label: string;
  value: string;
};

const subscribeToBrowserLanguage = () => () => undefined;
const getServerRegion = () => '';

const getBrowserRegion = () => {
  try {
    return new Intl.Locale(navigator.language).region ?? '';
  } catch {
    return '';
  }
};

const getInputId = (formId: string, name: string) => 'hubspot-' + formId + '-' + name.replaceAll('/', '-');

const getCountryFlag = (countryCode: string) =>
  countryCode.replace(/[A-Z]/g, (character) => String.fromCodePoint(127397 + character.charCodeAt(0)));

const ErrorMessage = ({ error, id }: { error?: string; id: string }) =>
  error ? (
    <span className={styles.fieldError} id={id}>
      {error}
    </span>
  ) : null;

const Label = ({ htmlFor, label, required }: { htmlFor: string; label: string; required?: boolean }) => (
  <label className={styles.label} htmlFor={htmlFor}>
    {label}
    {required && (
      <span className={styles.required} aria-hidden='true'>
        *
      </span>
    )}
  </label>
);

export const TextField = ({
  error,
  formId,
  label,
  name,
  required,
  type = 'text'
}: SharedFieldProps & { type?: 'email' | 'text' }) => {
  const inputId = getInputId(formId, name);
  const errorId = inputId + '-error';

  return (
    <div className={styles.field}>
      <Label htmlFor={inputId} label={label} required={required} />
      <input
        aria-describedby={error ? errorId : undefined}
        aria-invalid={Boolean(error)}
        aria-label={required ? label + '*' : label}
        autoComplete={
          name.endsWith('/firstname')
            ? 'given-name'
            : name.endsWith('/lastname')
              ? 'family-name'
              : name.endsWith('/email')
                ? 'email'
                : name.endsWith('/name')
                  ? 'organization'
                  : name.endsWith('/jobtitle')
                    ? 'organization-title'
                    : undefined
        }
        className={styles.input}
        id={inputId}
        maxLength={type === 'email' ? 254 : 255}
        name={name}
        required={required}
        type={type}
      />
      <ErrorMessage error={error} id={errorId} />
    </div>
  );
};

export const SelectField = ({
  error,
  formId,
  label,
  lazyOptions = false,
  name,
  options,
  required
}: SharedFieldProps & { lazyOptions?: boolean; options: readonly SelectOption[] }) => {
  const [optionsVisible, setOptionsVisible] = useState(!lazyOptions);
  const inputId = getInputId(formId, name);
  const errorId = inputId + '-error';

  return (
    <div className={styles.field}>
      <Label htmlFor={inputId} label={label} required={required} />
      <select
        aria-describedby={error ? errorId : undefined}
        aria-invalid={Boolean(error)}
        aria-label={required ? label + '*' : label}
        className={styles.select}
        defaultValue=''
        id={inputId}
        name={name}
        onFocus={() => setOptionsVisible(true)}
        onMouseDown={() => setOptionsVisible(true)}
        required={required}
      >
        <option value='' disabled={required} />
        {(optionsVisible ? options : []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ErrorMessage error={error} id={errorId} />
    </div>
  );
};

export const TextareaField = ({ error, formId, label, name, required }: SharedFieldProps) => {
  const inputId = getInputId(formId, name);
  const errorId = inputId + '-error';

  return (
    <div className={styles.field + ' ' + styles.textareaField}>
      <Label htmlFor={inputId} label={label} required={required} />
      <textarea
        aria-describedby={error ? errorId : undefined}
        aria-invalid={Boolean(error)}
        aria-label={required ? label + '*' : label}
        className={styles.textarea}
        id={inputId}
        maxLength={5000}
        name={name}
        required={required}
      />
      <ErrorMessage error={error} id={errorId} />
    </div>
  );
};

export const PhoneField = ({
  countryLabel,
  error,
  formId,
  label,
  language,
  name
}: SharedFieldProps & { countryLabel: string; language: SupportedLanguage }) => {
  const browserRegion = useSyncExternalStore(subscribeToBrowserLanguage, getBrowserRegion, getServerRegion);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [countriesVisible, setCountriesVisible] = useState(false);
  const countryCode = selectedCountry ?? browserRegion;
  const currentCountry = PHONE_COUNTRIES.find((country) => country.code === countryCode);
  const prefix = currentCountry?.prefix ?? '';
  const validatesPhoneNumber = phoneNumber !== null && phoneNumber !== '' && phoneNumber !== prefix;
  const inputId = getInputId(formId, name);
  const errorId = inputId + '-error';

  return (
    <div className={styles.field}>
      <Label htmlFor={inputId} label={label} />
      <div className={styles.phoneField}>
        <select
          aria-label={countryLabel}
          className={styles.select + ' ' + styles.phoneCountry}
          onFocus={() => setCountriesVisible(true)}
          onMouseDown={() => setCountriesVisible(true)}
          onChange={(event) => {
            const nextCountry = event.target.value;
            const nextPrefix = PHONE_COUNTRIES.find((country) => country.code === nextCountry)?.prefix ?? '';
            setSelectedCountry(nextCountry);
            setPhoneNumber(nextPrefix);
          }}
          value={countryCode}
        >
          <option value=''>{countryLabel}</option>
          {!countriesVisible && currentCountry && (
            <option value={currentCountry.code}>
              {getCountryFlag(currentCountry.code)}{' '}
              {getLocalizedCountryName(language, currentCountry.code, currentCountry.name)} ({currentCountry.prefix})
            </option>
          )}
          {(countriesVisible ? PHONE_COUNTRIES : []).map((country) => (
            <option key={country.code} value={country.code}>
              {getCountryFlag(country.code)} {getLocalizedCountryName(language, country.code, country.name)} (
              {country.prefix})
            </option>
          ))}
        </select>
        <input
          aria-describedby={error ? errorId : undefined}
          aria-invalid={Boolean(error)}
          aria-label={label}
          autoComplete='tel'
          className={styles.input}
          data-dial-prefix={prefix}
          id={inputId}
          inputMode='tel'
          maxLength={30}
          name={name}
          onChange={(event) => setPhoneNumber(event.target.value.replace(/[^0-9+(). -]/g, ''))}
          pattern={validatesPhoneNumber ? '[+0-9(). -]{7,30}' : undefined}
          type='tel'
          value={phoneNumber ?? prefix}
        />
      </div>
      <ErrorMessage error={error} id={errorId} />
    </div>
  );
};
